import Analytics from '@hh.ru/analytics-js';
import { Cookies } from '@hh.ru/browser-api-utils';

enum MessageType {
    Style = 'style',
    Image = 'image',
    Opacity = 'opacity',
    Class = 'class',
    Size = 'size',
    Info = 'info',
    Scroll = 'scroll',
    Position = 'position',
    Listener = 'listener',
    Event = 'event',
    History = 'history',
    LocationListener = 'locationListener',
    AddScroll = 'addScroll',
    RemoveScroll = 'removeScroll',
    ShowWidgetIcon = 'showWidgetIcon',
    ClientHeight = 'clientHeight',
    Properties = 'properties',
}

type MessagePayloadType = null | string | number | Record<string, unknown>;

interface MessageData {
    type: MessageType;
    payload: MessagePayloadType;
}

interface UxfeedbackParams {
    iframeSrc: string;
    iframeOrigin: string;
    sendEventsOnLoad: string[];
    additionalParams: Record<string, string>;
}

const isString = (payload: MessagePayloadType): payload is string => {
    return typeof payload === 'string';
};

const isNumber = (payload: MessagePayloadType): payload is number => {
    return typeof payload === 'number';
};

const isSize = (payload: MessagePayloadType): payload is { width?: string; height?: string } => {
    return payload !== null && typeof payload === 'object' && !!(payload.width || payload.height);
};

const getRole = () => {
    const role = Cookies.get('hhrole') ?? '';
    return ['employer', 'anonymous', 'applicant'].includes(role) ? role : '';
};

const Uxfeedback = (params: UxfeedbackParams): void => {
    const cryptedId = Cookies.get('crypted_id') ?? '';
    const cryptedHhuid = Cookies.get('crypted_hhuid') ?? '';
    const role = getRole();

    // Дополнительные параметры, передаваемые в UxFeedback
    const additionalParams = params.additionalParams;

    const iframe = document.createElement('iframe');
    iframe.src = params.iframeSrc;
    iframe.classList.add('uxfeedback-widget');
    iframe.sandbox.add('allow-scripts', 'allow-same-origin', 'allow-forms', 'allow-popups');
    document.body.appendChild(iframe);

    const setIframeStyle = (style: string) => {
        iframe.setAttribute('style', style);
    };

    const sendMessage = (type: MessageType, payload: MessagePayloadType) => {
        iframe.contentWindow?.postMessage({ type: `uxfb/${type}`, payload }, '*');
    };

    // События, которые нужно отправить по готовности ифрейма.
    const sendEventsOnLoad: string[] = params.sendEventsOnLoad;

    // Не теряем события, отправленные между созданием ифрейма
    // и моментом, когда он готов принимать сообщения.
    let uxFeedbackReady = false;
    const sendMessagesOnLoad = () => {
        sendEventsOnLoad.forEach((event) => {
            sendMessage(MessageType.Event, { event });
        });
    };

    const showWidgetIcon = (width: string, height: string) => {
        iframe.setAttribute('width', width);
        iframe.setAttribute('height', height);
        iframe.className = '';
        iframe.style.opacity = '1';
        sendMessage(MessageType.ShowWidgetIcon, {});
    };

    const setIframeOpacity = (opacity: number) => {
        iframe.style.opacity = `${opacity}`;
    };

    const addClassToIframe = (className: string) => {
        iframe.classList.add(className);
    };

    const setIframeSize = (width: string, height: string) => {
        if (width) {
            iframe.style.width = width;

            if (width === '100%') {
                iframe.style.left = '';
                iframe.style.right = '';
                iframe.style.top = '';
                iframe.style.transform = '';
            }
        }

        if (height) {
            iframe.style.height = height;
        }
    };

    const setIframePosition = () => {
        iframe.style.transform = '';
        iframe.style.left = '';
        iframe.style.right = '0px';
        iframe.style.top = ``;
        iframe.style.bottom = '0px';
        iframe.style.position = 'fixed';
        iframe.style.zIndex = '30';
    };

    const sendCustomProperties = () => {
        sendMessage(MessageType.Properties, { cryptedId, cryptedHhuid, ...additionalParams });
    };

    const sendCurrentWinInfo = () => {
        const clientHeight = window.innerHeight || document.documentElement.clientHeight;

        sendMessage(MessageType.Info, {
            url: document.location.href,
            cookies: `role=${role}`,
            devicePixelRatio: window.devicePixelRatio,
            scrollTop: document.documentElement.scrollTop,
            scrollHeight: document.documentElement.scrollHeight,
        });
        sendMessage(MessageType.ClientHeight, clientHeight);

        window.addEventListener(
            'resize',
            () => {
                const clientHeight = window.innerHeight || document.documentElement.clientHeight;
                sendMessage(MessageType.ClientHeight, clientHeight);
            },
            false
        );
    };

    const initLocationListener = () => {
        let previousHref = location.href;
        const observer = new MutationObserver(() => {
            const newHref = location.href;
            if (newHref !== previousHref) {
                sendMessage(MessageType.History, {
                    oldLocation: previousHref,
                    location: newHref,
                });
                previousHref = newHref;
            }
        });
        const config = { subtree: true, childList: true };
        observer.observe(document, config);
    };

    const scrollPageListener = () => {
        sendMessage(MessageType.Scroll, { scrollTop: document.documentElement.scrollTop });
    };

    window.addEventListener('message', (e: MessageEvent<MessageData>) => {
        const { type = '', payload } = e.data;
        const [prefix, name] = type.split('/');

        if (e.origin !== params.iframeOrigin || prefix !== 'uxfb') {
            return;
        }

        if (name === MessageType.Style && isString(payload)) {
            setIframeStyle(payload);
            return;
        }

        if (name === MessageType.Opacity && isNumber(payload)) {
            const isOpen = payload === 1;

            if (isOpen) {
                Analytics.sendHHEvent('screen_shown', {
                    hhtmSource: 'ux_feedback',
                    cryptedId,
                    cryptedHhuid,
                });
            }

            setIframeOpacity(payload);
            return;
        }

        if (name === MessageType.Class && isString(payload)) {
            addClassToIframe('uxs-slide-back');
            addClassToIframe(payload);
            return;
        }

        if (name === MessageType.Image && isSize(payload)) {
            const { width = '', height = '' } = payload;
            showWidgetIcon(width, height);
            return;
        }

        if (name === MessageType.Size && isSize(payload)) {
            const { width = '', height = '' } = payload;
            setIframeSize(width, height);
            return;
        }

        if (name === MessageType.Position && isSize(payload)) {
            setIframePosition();
            return;
        }

        if (name === MessageType.Listener) {
            uxFeedbackReady = true;
            sendCustomProperties();
            sendCurrentWinInfo();
            // tempexp_20518_start
            // Шлем событие о готовности UXFeedback, чтобы в скриптах можно было начать обработку данных,
            // по результатам которой, может быть отрисован опрос
            window.dispatchEvent(new CustomEvent('HH-UXFeedback-Mounted'));
            // tempexp_20518_end
            sendMessagesOnLoad();
            return;
        }

        if (name === MessageType.LocationListener) {
            initLocationListener();
            return;
        }

        if (name === MessageType.AddScroll) {
            window.addEventListener('scroll', scrollPageListener);
            return;
        }

        if (name === MessageType.RemoveScroll) {
            window.removeEventListener('scroll', scrollPageListener);
        }
    });

    // // TODO: перевести на HH-UXFeedback-Open https://jira.hh.ru/browse/PORTFOLIO-17697
    // window.addEventListener('HH-EmployerSuccessRegistration', () => {
    //     sendMessage(MessageType.Event, { event: 'successRegistration' });
    // });

    window.addEventListener('HH-UXFeedback-Open', (e) => {
        const event = (e as CustomEvent).detail as string;
        if (uxFeedbackReady) {
            sendMessage(MessageType.Event, { event });
        } else {
            sendEventsOnLoad.push(event);
        }
    });
};

export const initUxfeedback = (): void => {
    const uxfeedbackelem: HTMLDivElement | null = document.querySelector('#uxfeedback_config');
    if (uxfeedbackelem?.textContent) {
        try {
            const config: UxfeedbackParams = JSON.parse(
                uxfeedbackelem.textContent as unknown as string
            ) as UxfeedbackParams;
            Uxfeedback(config);
        } catch {
            console.error('ERROR: while initializing uxfeedback');
        }
    }
};

export const uxfeedbackEvents = {
    professionPageCalculatorUsed(): void {
        window.dispatchEvent(
            new CustomEvent('HH-UXFeedback-Open', {
                detail: 'profession_page_calculator_used',
            })
        );
    },
    professionBasedSurvey(): void {
        window.dispatchEvent(
            new CustomEvent('HH-UXFeedback-Open', {
                detail: `profession_based_survey`,
            })
        );
    },
};
